import { createFileRoute, useNavigate, redirect, Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { mfaChallengeRequired } from '@/lib/mfa'
import { btnPrimary, btnSecondary } from '@/components/rm'
import { BrandMark, BRAND_URL } from '@/components/brand'
import { LanguageMenu, useT } from '@/i18n'
import { displayPhMobile, normalisePhMobile } from '@/lib/ph'
import { landingPath } from '@/lib/account'

// Spec 002. A mobile number and an SMS code is the main way in, for clients and trade persons
// alike. Email and password stays for business owners who signed up that way.
export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({ next: typeof search.next === 'string' && (search.next === '/ops' || search.next.startsWith('/mcp/authorize?request_id=')) ? search.next : undefined }),
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession()
    if (data.session) throw redirect({ to: await landingPath() })
  },
  component: LoginPage,
})

const inputCls =
  'h-11 w-full rounded-[10px] border border-token-subtle px-3 text-[15px] text-token-primary outline-none transition-colors focus:border-token-strong focus:ring-2 focus:ring-brand/10'

const RESEND_SECONDS = 60
const GOOGLE_ENABLED = import.meta.env.VITE_GOOGLE_AUTH_ENABLED === 'true'

type Mode = 'mobile' | 'code' | 'email' | 'forgot'

function LoginPage() {
  const navigate = useNavigate()
  const { t } = useT()
  const { next } = Route.useSearch()
  const [mode, setMode] = useState<Mode>('mobile')
  const [mobile, setMobile] = useState('')
  const [e164, setE164] = useState('')
  const [code, setCode] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)
  const [wait, setWait] = useState(0)

  useEffect(() => {
    if (wait <= 0) return
    const id = setTimeout(() => setWait((w) => w - 1), 1000)
    return () => clearTimeout(id)
  }, [wait])

  const go = (m: Mode) => { setMode(m); setError(''); setNotice('') }

  const afterSignIn = async () => {
    const needsCode = await mfaChallengeRequired()
    if (needsCode) return navigate({ to: '/mfa-challenge', search: next === '/ops' ? { next: '/ops' } : {} })
    if (next) return window.location.assign(next)
    navigate({ to: await landingPath() })
  }

  const sendCode = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setError('')
    const n = normalisePhMobile(mobile)
    if (!n) return setError(t('mobile.notPh'))
    setBusy(true)
    const { error: err } = await supabase.auth.signInWithOtp({ phone: n })
    setBusy(false)
    if (err) return setError(t('mobile.sendFailed'))
    setE164(n); setCode(''); setWait(RESEND_SECONDS); go('code')
  }

  const verify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const token = code.replace(/\D/g, '')
    if (token.length !== 6) return setError(t('mobile.wrongCode'))
    setBusy(true)
    const { error: err } = await supabase.auth.verifyOtp({ phone: e164, token, type: 'sms' })
    if (err) { setBusy(false); return setError(t('mobile.wrongCode')) }
    await afterSignIn()
    setBusy(false)
  }

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(''); setBusy(true)
    const { error: err } = await supabase.auth.signInWithPassword({ email, password })
    if (err) { setBusy(false); return setError(t('signIn.badLogin')) }
    await afterSignIn()
    setBusy(false)
  }

  const sendReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(''); setNotice('')
    if (!email) return setError(t('reset.enterEmail'))
    setBusy(true)
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset-password` })
    setBusy(false)
    if (err) return setError(err.message)
    setNotice(t('reset.sent'))
  }

  const google = () => supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: `${window.location.origin}/` } })

  const title = mode === 'code' ? t('mobile.codeTitle') : mode === 'email' ? t('signIn.title') : mode === 'forgot' ? t('reset.title') : t('mobile.title')
  const subtitle = mode === 'code' ? t('mobile.codeSent', { mobile: displayPhMobile(e164) }) : mode === 'email' ? t('signIn.subtitle') : mode === 'forgot' ? t('reset.subtitle') : t('mobile.subtitle')

  return (
    <div className="flex min-h-screen items-start justify-center bg-canvas-subtle px-4 py-10 sm:items-center">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="mb-4 flex justify-center"><LanguageMenu /></div>
          <BrandMark className="mx-auto mb-3 h-11 w-11" />
          <h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">{title}</h1>
          <p className="mt-1 text-[13.5px] text-token-secondary">{subtitle}</p>
        </div>

        {mode === 'mobile' && (
          <form onSubmit={sendCode} className="space-y-4 rounded-[16px] border border-token-subtle bg-token-surface p-6">
            <div className="space-y-1.5">
              <label htmlFor="mobile" className="text-[13px] font-semibold text-token-secondary">{t('mobile.label')}</label>
              <input id="mobile" type="tel" inputMode="tel" autoComplete="tel-national" value={mobile} onChange={(e) => setMobile(e.target.value)} className={inputCls} placeholder={t('mobile.placeholder')} />
            </div>
            {error && <p role="alert" className="text-[13px] text-token-danger">{error}</p>}
            <button type="submit" disabled={busy} className={`${btnPrimary} h-11 w-full justify-center`}>{busy ? t('mobile.sending') : t('mobile.send')}</button>
            {GOOGLE_ENABLED && (
              <>
                <div className="flex items-center gap-3 text-[12px] text-token-muted"><span className="h-px flex-1 bg-token-subtle" />{t('mobile.or')}<span className="h-px flex-1 bg-token-subtle" /></div>
                <button type="button" onClick={google} className={`${btnSecondary} h-11 w-full justify-center`}>{t('mobile.google')}</button>
              </>
            )}
            <button type="button" onClick={() => go('email')} className="w-full pt-1 text-center text-[13px] font-semibold text-token-primary hover:underline">{t('mobile.useEmail')}</button>
            <Terms />
          </form>
        )}

        {mode === 'code' && (
          <form onSubmit={verify} className="space-y-4 rounded-[16px] border border-token-subtle bg-token-surface p-6">
            <div className="space-y-1.5">
              <label htmlFor="code" className="text-[13px] font-semibold text-token-secondary">{t('mobile.codeLabel')}</label>
              <input id="code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className={`${inputCls} text-center font-display text-[22px] tracking-[0.4em]`} placeholder="••••••" />
            </div>
            {error && <p role="alert" className="text-[13px] text-token-danger">{error}</p>}
            <button type="submit" disabled={busy} className={`${btnPrimary} h-11 w-full justify-center`}>{busy ? t('mobile.verifying') : t('mobile.verify')}</button>
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pt-1 text-[13px]">
              <button type="button" onClick={() => go('mobile')} className="font-semibold text-token-primary hover:underline">{t('mobile.changeNumber')}</button>
              {wait > 0
                ? <span className="text-token-muted">{t('mobile.resendIn', { s: wait })}</span>
                : <button type="button" onClick={() => { void sendCode() }} className="font-semibold text-token-primary hover:underline">{t('mobile.resend')}</button>}
            </div>
          </form>
        )}

        {mode === 'email' && (
          <form onSubmit={signIn} className="space-y-4 rounded-[16px] border border-token-subtle bg-token-surface p-6">
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-[13px] font-semibold text-token-secondary">{t('common.email')}</label>
              <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" className={inputCls} placeholder={t('common.emailPlaceholder')} />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-[13px] font-semibold text-token-secondary">{t('common.password')}</label>
                <button type="button" onClick={() => go('forgot')} className="text-[12.5px] font-semibold text-token-primary hover:underline">{t('signIn.forgot')}</button>
              </div>
              <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" className={inputCls} placeholder="••••••••" />
            </div>
            {error && <p role="alert" className="text-[13px] text-token-danger">{error}</p>}
            <button type="submit" disabled={busy} className={`${btnPrimary} h-11 w-full justify-center`}>{busy ? t('signIn.busy') : t('signIn.button')}</button>
            <button type="button" onClick={() => go('mobile')} className="w-full text-center text-[13px] font-semibold text-token-primary hover:underline">{t('mobile.useMobile')}</button>
            <p className="text-center text-[13px] text-token-secondary">
              {t('signIn.newHere')} <Link to="/signup" className="font-semibold text-token-primary hover:underline">{t('signIn.createAccount')}</Link>
            </p>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={sendReset} className="space-y-4 rounded-[16px] border border-token-subtle bg-token-surface p-6">
            <div className="space-y-1.5">
              <label htmlFor="remail" className="text-[13px] font-semibold text-token-secondary">{t('common.email')}</label>
              <input id="remail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" className={inputCls} placeholder={t('common.emailPlaceholder')} />
            </div>
            {error && <p role="alert" className="text-[13px] text-token-danger">{error}</p>}
            {notice && <p className="text-[13px] text-token-success">{notice}</p>}
            <button type="submit" disabled={busy} className={`${btnPrimary} h-11 w-full justify-center`}>{busy ? t('reset.busy') : t('reset.button')}</button>
            <button type="button" onClick={() => go('email')} className="w-full text-center text-[13px] font-semibold text-token-primary hover:underline">{t('reset.back')}</button>
          </form>
        )}
      </div>
    </div>
  )
}

function Terms() {
  const { t } = useT()
  // The sentence order differs between languages, so the links are placed into it.
  const [before, middle, after] = t('mobile.terms', { terms: '\u0000', privacy: '\u0001' }).split(/[\u0000\u0001]/)
  return (
    <p className="text-center text-[11.5px] leading-relaxed text-token-secondary">
      {before}<a href={`${BRAND_URL}/terms`} className="underline">{t('signUp.terms')}</a>{middle}<a href={`${BRAND_URL}/privacy`} className="underline">{t('signUp.privacy')}</a>{after}
    </p>
  )
}
